import { LoaderFunctionArgs } from "react-router-dom";
import { PricingPlan } from "~/components/Cards/PricingCard";
import { apiClient, LandingPageLoaderResult } from "~/services/apiClient";
import {
  captureAffiliateRefFromUrl,
  clearAffiliateVisitPending,
} from "~/utils/auth/affiliateRefStorage";

export type LandingPageLoaderType = {
  plans: PricingPlan[];
};

/**
 * Explore page loader (`/explore`)
 *
 * Fetches active subscription plans from the Lambda API and maps them
 * to the PricingPlan format for display on the Explore page.
 * No authentication required - public endpoint.
 * Captures ?ref= for affiliate attribution; visit logging happens in the
 * same landing-page backend call when ref is present.
 */
export async function LandingPageLoader(
  args: LoaderFunctionArgs,
): Promise<LandingPageLoaderType> {
  const url = new URL(args.request.url);
  const refFromUrl = captureAffiliateRefFromUrl(args.request.url);

  const result = await apiClient.loader<LandingPageLoaderResult>(
    "landing-page",
    refFromUrl
      ? {
          ref: refFromUrl,
          path: `${url.pathname}${url.search}`,
        }
      : undefined,
  );

  // Visit was recorded (or attempted) with landing-page; don't also record on sign-in.
  if (refFromUrl) {
    clearAffiliateVisitPending();
  }

  if (!result.success || !result.data) {
    return { plans: [] };
  }

  return {
    plans: result.data.plans,
  };
}
